import { useEffect, useRef, useState, type JSX } from "react";
import { dialerSessionActiveDetailsSchema, dialerSourceKey, type DialerSessionActiveDetails, type DialerSessionView, type DialerSource, type SavedDialerSession } from "@gioia365/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { ApiRequestError } from "../api/http";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { Reason } from "../design/components/Reason";
import { messageForError } from "../i18n/errors";
import { formatCount, formatNumericDateTime } from "../i18n/format";
import { useT, type Translate } from "../i18n/locale";
import type { Access } from "../session/access";
import { commercialViewOf, tabKey, type TabContext } from "../session/tab-context";
import { callErrorText, reasonText } from "../telephony/call-texts";
import { dialerKeys, interruptDialerSessionById, requestDialerSession } from "./dialer-queries";
import { useOptionalDialerSession, type DeskBlock } from "./dialer-session";
import { dialerLeadName, sourceText } from "./dialer-texts";
import { useSourcePeople } from "./source-people";

// «Inizia a chiamare», «Riprendi a chiamare» e «Ricomincia da capo» di una pagina sorgente del Power Dialer: la Smart List (piano
// M5, Task 6) e, da R4, i Task (Task 21). Le due pagine hanno le stesse regole e qui sta ciò che le accomuna: perché da questa
// scheda non si chiama (la «Guarda come…», il contesto di piattaforma, senza «Chiamare e usare la postazione»: ruling Q27), la riga
// della sessione salvata, il rifiuto dell'avvio con le parole di «Chiama» (telefonia spenta, ruling Q10; senza il cellulare), la
// sessione viva sulla stessa sorgente (ruling Q47: niente si avvia, «Sessione in corso su …» con «Torna alla scheda») e su
// un'altra (`409 dialer_session_active`: la finestra chiede se interromperla per chiamare da qui). La coda occupata (`409
// dialer_queue_busy`, ruling Q28) la riprova il guscio da sé (`DialerSessionProvider.start`).

export type StartMode = "start" | "resume" | "restart";

/**
 * Perché da questa scheda non si chiama in cascata, qualunque sia la sorgente: nella «Guarda come…» e nel contesto di piattaforma
 * con le parole di «Chiama» della scheda (ruling Q27), senza «Chiamare e usare la postazione» con la sua riga. `null` quando si
 * chiama: le stesse condizioni del Power Dialer del guscio, che soltanto allora legge e scrive le sessioni.
 */
export function callBlockOf(t: Translate, access: Access, tab: TabContext): string | null {
  if (commercialViewOf(tab) !== null) return reasonText(t, "commercial_view", null);
  if (tab.kind !== "membership" || access.isPlatform) return reasonText(t, "platform_context", null);
  if (!access.can("call")) return t("smartLists.page.startNoCall");
  return null;
}

/**
 * «Sessione interrotta il …»: quando, quanti Lead fatti e da chi si riparte; senza nessuno da cui ripartire, `empty` (le parole
 * della sorgente vuota).
 */
export function savedText(t: Translate, locale: Parameters<typeof formatCount>[0], timeZone: string, saved: SavedDialerSession, empty: string): string {
  if (saved.next === null) return empty;
  const values = { when: formatNumericDateTime(timeZone, saved.interruptedAt), next: dialerLeadName(saved.next) };
  if (saved.done === 0) return t("smartLists.page.savedLineNone", values);
  if (saved.done === 1) return t("smartLists.page.savedLineOne", values);
  return t("smartLists.page.savedLine", { ...values, done: formatCount(locale, saved.done) });
}

/** Il rifiuto dell'avvio: i motivi delle chiamate con le parole di «Chiama» (telefonia spenta, numero da verificare), gli altri i loro. */
export function startErrorText(t: Translate, error: unknown): string {
  return error instanceof ApiRequestError ? callErrorText(t, error) : messageForError(t, error);
}

const isLive = (session: DialerSessionView) => session.state === "active" || session.state === "paused";
const sameSource = (first: DialerSource, second: DialerSource) => dialerSourceKey(first) === dialerSourceKey(second);

/** La finestra «Hai una cascata in corso su «…»», con ciò che serve a mostrarla. */
export type OtherSessionPrompt = {
  /** Di dove è nata la sessione viva: i Task si nominano dal catalogo, con la persona (`sourceText`). */
  source: DialerSource;
  /** Il nome che il server ha salvato con la sessione: quello di una Smart List (l'istantanea dell'audit per i Task non si mostra). */
  sourceName: string;
  /** La scheda di chi guarda, per leggere le persone di «Di chi» che il nome nomina. */
  tab: TabContext;
  /** Perché «Interrompi e chiama da qui» aspetta: la chiamata del Lead aperto, o l'esito e i passi (ruling Q46). */
  blocked: DeskBlock | null;
  switching: boolean;
  error: unknown;
  confirm(): void;
  close(): void;
};

/**
 * L'avvio del Power Dialer da una pagina sorgente. `caller` è la scheda di chi chiama da qui (`null` dove non si chiama:
 * `callBlockOf`), `source` la sorgente della pagina (`null` dove la pagina adesso non ne ha una). `onLiveEnded` è detto quando la
 * sessione viva della barra finisce o cambia (interrotta dalla barra, finita, interrotta dal giro del server): la sessione salvata
 * della pagina può essere lei, e si rilegge. `onRefused` quando l'avvio è rifiutato (la sessione salvata può essere cambiata
 * intanto, da un'altra scheda), `onStarted` quando è partito.
 */
export function useDialerStart(input: {
  caller: TabContext | null;
  source: DialerSource | null;
  onLiveEnded: () => void;
  onRefused: () => void;
  onStarted?: () => void;
}) {
  const { caller, source } = input;
  const dialer = useOptionalDialerSession();
  const queryClient = useQueryClient();
  const [starting, setStarting] = useState<StartMode | null>(null);
  // Il rifiuto dell'avvio vale per la sorgente in cui è nato: cambiando scheda o persona (o lista) non si vede più.
  const sourceKey = source ? dialerSourceKey(source) : null;
  const [refusal, setRefusal] = useState<{ error: unknown; sourceKey: string | null } | null>(null);
  if (refusal !== null && refusal.sourceKey !== sourceKey) setRefusal(null);
  const startError = refusal?.error ?? null;
  const setStartError = (error: unknown) => setRefusal(error === null ? null : { error, sourceKey });
  const [otherSession, setOtherSession] = useState<{ details: DialerSessionActiveDetails; source: DialerSource; tab: TabContext; mode: StartMode } | null>(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<unknown>(null);

  // La sessione viva della barra, se è su questa sorgente (ruling Q47): al posto dei pulsanti d'avvio, si torna al suo Lead.
  const liveSession = dialer?.session ?? null;
  const liveHere = caller !== null && source !== null && liveSession !== null && isLive(liveSession) && sameSource(liveSession.source, source) ? liveSession : null;

  // La sessione viva del guscio finisce mentre la pagina è aperta: quella salvata di questa sorgente può essere lei.
  const liveId = liveSession?.id ?? null;
  const seenLive = useRef(liveId);
  const callerKey = caller ? tabKey(caller) : null;
  const onLiveEnded = useRef(input.onLiveEnded);
  useEffect(() => {
    onLiveEnded.current = input.onLiveEnded;
  });
  useEffect(() => {
    const before = seenLive.current;
    seenLive.current = liveId;
    if (before === null || before === liveId || callerKey === null) return;
    onLiveEnded.current();
  }, [liveId, callerKey]);

  async function startCalling(mode: StartMode) {
    if (!dialer || !caller || !source || starting !== null) return;
    setStartError(null);
    setStarting(mode);
    try {
      // Il guscio avvia la sessione (riprova da sé la coda occupata, ruling Q28) e apre la scheda del primo Lead.
      await dialer.start({ tab: caller, source, mode });
      input.onStarted?.();
    } catch (error) {
      const active = error instanceof ApiRequestError && error.code === "dialer_session_active" ? dialerSessionActiveDetailsSchema.safeParse(error.details) : null;
      if (active?.success) await askAboutLive(active.data, mode);
      else {
        setStartError(error);
        input.onRefused();
      }
    } finally {
      setStarting(null);
    }
  }

  /**
   * Una sessione viva che la barra di questa scheda non conosce (nata in un'altra scheda del browser): se è su questa sorgente,
   * nessuna finestra (ruling Q47): la barra la rilegge dal server e la pagina offre di tornarci; altrimenti la finestra.
   */
  async function askAboutLive(details: DialerSessionActiveDetails, mode: StartMode) {
    if (!caller || !source) return;
    // Di dove è nata la sessione viva: quella della barra, o quella letta adesso dal server.
    const barSession = dialer?.session ?? null;
    let liveSource = barSession !== null && barSession.id === details.sessionId ? barSession.source : null;
    if (liveSource === null) {
      const live = await requestDialerSession(caller, details.sessionId).catch((error: unknown) => {
        setStartError(error);
        return null;
      });
      if (live === null) return;
      if (sameSource(live.source, source)) {
        void queryClient.invalidateQueries({ queryKey: dialerKeys.current(tabKey(caller)) });
        return;
      }
      liveSource = live.source;
    }
    setOtherSession({ details, source: liveSource, tab: caller, mode });
  }

  /**
   * «Interrompi e chiama da qui»: la sessione viva si interrompe (resta salvata sulla sua sorgente), poi parte questa. Se è quella
   * della barra la interrompe la barra, che chiude anche la postazione di M4, e il suo riepilogo non si mostra: si va avanti da
   * qui; se è nata in un'altra scheda del browser, con la sua versione letta adesso.
   */
  async function interruptOtherAndStart() {
    if (!otherSession || !dialer || !caller || switching) return;
    const { details, mode } = otherSession;
    setSwitching(true);
    setSwitchError(null);
    try {
      if (dialer.session?.id === details.sessionId) {
        await dialer.interrupt();
        dialer.closeSummary();
      } else {
        await interruptDialerSessionById(caller, details.sessionId);
      }
    } catch (error) {
      setSwitchError(error);
      setSwitching(false);
      return;
    }
    setSwitching(false);
    setOtherSession(null);
    await startCalling(mode);
  }

  function closeOtherSession() {
    if (switching) return;
    setOtherSession(null);
    setSwitchError(null);
  }

  const other: OtherSessionPrompt | null =
    otherSession === null
      ? null
      : {
          source: otherSession.source,
          sourceName: otherSession.details.sourceName,
          tab: otherSession.tab,
          blocked: dialer?.session?.id === otherSession.details.sessionId ? dialer.interruptBlocked : null,
          switching,
          error: switchError,
          confirm: () => void interruptOtherAndStart(),
          close: closeOtherSession,
        };

  return { dialer, liveHere, starting, startError, startCalling, other };
}

/** «Hai una cascata in corso su «…»: la interrompi per chiamare da qui?», con «Annulla» e «Interrompi e chiama da qui». */
export function OtherSessionDialog({ other, id }: { other: OtherSessionPrompt; id: string }): JSX.Element {
  const t = useT();
  const people = useSourcePeople(other.tab, other.source);
  const blockedId = `${id}-other-blocked`;
  // Una Smart List si nomina con il nome che aveva; i Task dal catalogo, con la persona e senza virgolette attorno (questione 339).
  const title =
    other.source.type === "smart_list"
      ? t("smartLists.page.otherSession", { source: other.sourceName })
      : t("tasks.start.otherSession", { source: sourceText(t, other, people) });
  return (
    <Dialog
      open
      title={title}
      onClose={other.close}
      footer={
        <>
          <Button onClick={other.close} disabled={other.switching}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={other.switching || other.blocked !== null}
            aria-busy={other.switching ? "true" : undefined}
            aria-describedby={other.blocked !== null ? blockedId : undefined}
            onClick={other.confirm}
          >
            {t("smartLists.page.interruptOther")}
          </Button>
        </>
      }
    >
      {other.blocked !== null ? <Reason id={blockedId}>{other.blocked === "call" ? t("dialer.interruptInCall") : t("dialer.interruptBeforeOutcome")}</Reason> : null}
      {other.error ? (
        <Reason tone="warning" role="alert">
          {messageForError(t, other.error)}
        </Reason>
      ) : null}
    </Dialog>
  );
}
